import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import "firebase/storage";
import "firebase/messaging";

const getEnv = (key: string, fallback: string): string =>
  typeof process.env[key] !== "undefined" && process.env[key] !== ""
    ? (process.env[key] as string)
    : fallback;

function warnDev(message: string, detail?: unknown): void {
  if (process.env.NODE_ENV !== "production") {
    console.warn(message, detail !== undefined ? detail : "");
  }
}

/**
 * Token FCM; cadena vacía si el entorno no soporta messaging o getToken falla (p. ej. Safari iOS).
 */
export async function getFirebaseMessagingToken(vapidKey: string): Promise<string> {
  try {
    if (typeof firebase.messaging !== "function") {
      return "";
    }
    const messagingFactory = firebase.messaging as typeof firebase.messaging & {
      isSupported?: () => boolean;
    };
    if (
      typeof messagingFactory.isSupported === "function" &&
      !messagingFactory.isSupported()
    ) {
      return "";
    }
    const messaging = firebase.messaging();
    const token = await messaging.getToken({ vapidKey });
    return typeof token === "string" ? token : "";
  } catch (error: unknown) {
    warnDev("[Firebase] Messaging getToken no disponible; se continúa sin push token.", error);
    return "";
  }
}

function initFirebase(): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      if (!firebase.apps.length) {
        firebase.initializeApp({
          apiKey: getEnv("VUE_APP_FIREBASE_API_KEY", "AIzaSyB2QiUQfrQjuwSSdXF-Y7q5SBZCmPc33fg"),
          authDomain: getEnv("VUE_APP_FIREBASE_AUTH_DOMAIN", "control-gestion-e3e43.firebaseapp.com"),
          databaseURL: getEnv("VUE_APP_FIREBASE_DATABASE_URL", "https://control-gestion-e3e43-default-rtdb.firebaseio.com"),
          projectId: getEnv("VUE_APP_FIREBASE_PROJECT_ID", "control-gestion-e3e43"),
          storageBucket: getEnv("VUE_APP_FIREBASE_STORAGE_BUCKET", "control-gestion-e3e43.appspot.com"),
          messagingSenderId: getEnv("VUE_APP_FIREBASE_MESSAGING_SENDER_ID", "883044092638"),
          appId: getEnv("VUE_APP_FIREBASE_APP_ID", "1:883044092638:web:c7d73694b397802ecdd04f"),
        });
      }
    } catch (error: unknown) {
      reject(error instanceof Error ? error : new Error(String(error)));
      return;
    }

    let db: firebase.firestore.Firestore;
    try {
      db = firebase.firestore();
    } catch (error: unknown) {
      reject(error instanceof Error ? error : new Error(String(error)));
      return;
    }

    // Modo emulador (npm run serve:emulador): Firestore, Auth y Storage locales, nunca la base real.
    if (process.env.VUE_APP_USE_EMULATOR === "true") {
      db.useEmulator("127.0.0.1", Number(getEnv("VUE_APP_EMULATOR_FIRESTORE_PORT", "8085")));
      firebase.auth().useEmulator(`http://127.0.0.1:${getEnv("VUE_APP_EMULATOR_AUTH_PORT", "9099")}`);
      firebase.storage().useEmulator("127.0.0.1", Number(getEnv("VUE_APP_EMULATOR_STORAGE_PORT", "9199")));
      warnDev("[Firebase] Usando emuladores locales (VUE_APP_USE_EMULATOR=true).");
      // Sin caché offline: evita mezclar datos entre reinicios del emulador.
      resolve();
      return;
    }

    try {
      void db
        .enablePersistence()
        .then(() => {
          resolve();
        })
        .catch((err: unknown) => {
          // failed-precondition, unimplemented (Safari iOS), u otro: no bloquear arranque
          warnDev(
            "[Firebase] Firestore enablePersistence no disponible; continuando sin caché offline.",
            err
          );
          resolve();
        });
    } catch (syncErr: unknown) {
      warnDev(
        "[Firebase] Error al solicitar enablePersistence; continuando sin caché offline.",
        syncErr
      );
      resolve();
    }
  });
}

export { firebase, initFirebase };
